/**
 * 热点数据本地持久化缓存
 *
 * 使用 localStorage（而非 sessionStorage）确保数据在标签切换、
 * 页面刷新后依然可用，减少对后端 API 的重复请求。
 * 
 * TTL: 2 小时（与后端 DB 缓存窗口保持一致）
 */

const CACHE_PREFIX = 'contentos_hotspots_';
const CACHE_TTL_SECONDS = 2 * 60 * 60; // 2 hours

const getKey = (platform) => `${CACHE_PREFIX}${platform}`;

const readCache = (platform) => {
  try {
    const raw = localStorage.getItem(getKey(platform));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.data) || !parsed.cachedAt) return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

/**
 * 保存热点数据到本地缓存
 */
export const saveHotspotsToCache = (platform, data) => {
  if (!platform) return;
  try {
    const payload = JSON.stringify({
      data: Array.isArray(data) ? data : [],
      cachedAt: Date.now(),
    });
    localStorage.setItem(getKey(platform), payload);
    if (import.meta.env.DEV) {
      console.debug(`[HotspotCache] saved ${data.length} items for ${platform}`);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug('[HotspotCache] save failed:', error);
    }
  }
};

/**
 * 从本地缓存读取热点数据
 * 如果缓存存在且未过期则返回数据，否则返回 null
 */
export const getHotspotsFromCache = (platform) => {
  const cache = readCache(platform);
  if (!cache) return null;

  const ageSeconds = Math.floor((Date.now() - cache.cachedAt) / 1000);
  if (ageSeconds >= CACHE_TTL_SECONDS) {
    try {
      localStorage.removeItem(getKey(platform));
    } catch (error) {
      // Ignore cleanup failures.
    }
    return null;
  }

  return cache.data;
};

/**
 * 获取缓存元数据（用于 UI 显示）
 */
export const getCacheInfo = (platform) => {
  const cache = readCache(platform);
  if (!cache) return null;

  const ageSeconds = Math.floor((Date.now() - cache.cachedAt) / 1000);
  const remainingSeconds = Math.max(0, CACHE_TTL_SECONDS - ageSeconds);
  if (remainingSeconds <= 0) return null;

  return {
    cachedAt: new Date(cache.cachedAt).toLocaleString(),
    count: cache.data.length,
    remainingSeconds,
  };
};

/**
 * 清除所有热点缓存（调试用）
 */
export const clearAllHotspotCache = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    // Ignore.
  }
};
